import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  Send,
  Trash2,
  FileText,
  Info,
  Recycle,
  Camera,
} from "lucide-react";

const SectionGWasteManagement = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);

  const [form, setForm] = useState({
    g1: "",
    g2: "",
    g2Photo: null,
    g2Remarks: "",
    g3: "",
    g3Document: null,
    g3Remarks: "",
    g4: "",
    g4Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  const [previewImages, setPreviewImages] = useState({
    g2Photo: null,
    g3Document: null,
  });
  
  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_g");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          setForm(savedData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        const isPartial = determinePartialStatus();
        const dataToSave = {
          ...form,
          isPartial: isPartial,
          lastSaved: new Date().toISOString(),
        };
        localStorage.setItem("survey_section_g", JSON.stringify(dataToSave));
        setSaveStatus(t.global.alert_saved_locally);
        setTimeout(() => setSaveStatus(""), 2000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [form, t]);

  useEffect(() => {
    return () => {
      Object.values(previewImages).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previewImages]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({
        ...prev,
        [name]: file,
        ...(errors[name] && { [name]: undefined }),
      }));

      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      }
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        // Clear optional fields when answer changes
        ...((name === "g1" ||
          name === "g2" ||
          name === "g3" ||
          name === "g4") &&
          value === t.global.yes && {
            [`${name}Remarks`]: "",
            ...(name === "g2" && { g2Photo: null }),
            ...(name === "g3" && { g3Document: null }),
          }),
      }));
    }

    // Clear error when user starts typing/selecting
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Determine if section is partially completed
  const determinePartialStatus = () => {
    // Check basic required fields
    if (!form.g1 || !form.g2 || !form.g3 || !form.g4) {
      return false;
    }
    return false;
  };

  const validateForm = () => {
    const newErrors = {};

    // G1 validation - Required selection only
    if (!form.g1?.trim()) {
      newErrors.g1 = t.global.err_a1;
    }

    // G2 validation - Required selection only
    if (!form.g2?.trim()) {
      newErrors.g2 = t.global.err_a1;
    }

    // G3 validation - Required selection only
    if (!form.g3?.trim()) {
      newErrors.g3 = t.global.err_a1;
    }

    // G4 validation - Required selection only
    if (!form.g4?.trim()) {
      newErrors.g4 = t.global.err_a1;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.closest(".question-container")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || 1);
      payload.append("ward_id", state?.ward_id || 5);
      payload.append("district_id", state?.district_id || 10);
      payload.append(
        "lattitude_longitude",
        state?.lattitude_longitude || "22.5726,88.3639",
      );
      payload.append("program_name", state?.area_name || "demo kiosk");
      payload.append("current_section", "waste");
      payload.append("kiosk_location_fulladdress", state?.fullAddress || "N/A");
      payload.append("reporting_date", new Date().toISOString().split("T")[0]);
      payload.append("area_id", state?.area_id || state?.id);
      payload.append("canteen_type_id", state?.canteen_type || 2);

      // Append form data
      payload.append("wst_mngmnt_G1", yesNoToInt(form.g1));

      payload.append("wst_mngmnt_G2", yesNoToInt(form.g2));
      payload.append("wst_mngmnt_G2_remarks", form.g2Remarks || "");

      if (form.g2Photo) {
        payload.append("wst_mngmnt_G2_upload", form.g2Photo);
      }

      payload.append("wst_mngmnt_G3", yesNoToInt(form.g3));
      payload.append("wst_mngmnt_G3_remarks", form.g3Remarks || "");

      if (form.g3Document) {
        payload.append("wst_mngmnt_G3_upload", form.g3Document);
      }

      payload.append("wst_mngmnt_G4", yesNoToInt(form.g4));
      payload.append("wst_mngmnt_G4_remarks", form.g4Remarks || "");

      // Determine partial status
      const isPartial = determinePartialStatus();

      // Save section data with partial flag
      const sectionDataWithPartial = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        submittedAsPartial: isPartial,
      };

      // Always save the data
      localStorage.setItem(
        "survey_section_g",
        JSON.stringify(sectionDataWithPartial),
      );

      // Update completed sections tracking
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      if (!isPartial) {
        completedSections.g = true;
      } else {
        delete completedSections.g;
      }
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Store partial sections separately
      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      if (isPartial) {
        partialSections.g = true;
      } else {
        delete partialSections.g;
      }
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      // Dispatch Redux action
      const response = await dispatch(createCanteen(payload));

      // Navigate back to sections
      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error submitting Section G:", err);
      alert(t.global.alert_server_error);
      setIsSubmitting(false);
    }
  };

  const yesNoOptions = [t.global.yes, t.global.no];

  const getSurveyTypeDisplay = (type) => {
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return type || t.global.survey_type_default;
    }
  };

  // Calculate if form can be submitted
  const isFormSubmittable = form.g1 && form.g2 && form.g3 && form.g4;

  // Check if form will be submitted as partial
  const willBePartial = isFormSubmittable ? determinePartialStatus() : false;

  // Show completion status only when form is submittable
  const showCompletionStatus = isFormSubmittable;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.G.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.G.subtitle}
              </p>
              {saveStatus && (
                <div className="text-sm text-green-600 mt-1 flex items-center">
                  <CheckCircle size={14} className="mr-1" />
                  {saveStatus}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} {t.global.survey_type_default}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* Partial Submission Warning */}
          {showCompletionStatus && willBePartial && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">
                    Partial Submission
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    This section will be saved as{" "}
                    <span className="font-semibold">partially completed</span>{" "}
                    because:
                  </p>
                  <ul className="text-amber-700 text-sm mt-2 list-disc pl-5 space-y-1">
                    {form.g2 === t.global.yes && !form.g2Photo && (
                      <li>Photo for G2 is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                    {form.g3 === t.global.yes && !form.g3Document && (
                      <li>Document for G3 is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                  </ul>
                  <p className="text-amber-700 text-sm mt-2">
                    You can submit now and complete later, or add the missing
                    optional information for full completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* G1 - Waste Segregation Bins */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Recycle size={18} className="mr-2" />
                G1. {t.modules.kiosk.sections.G.questions.coloredBins}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.g1 === option
                          ? form.g1 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="g1"
                            value={option}
                            checked={form.g1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.g1 === option
                                ? form.g1 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.g1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g1}
                </p>
              )}
            </div>

            {/* G2 - IEC Display */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <FileText size={18} className="mr-2" />
                G2. {t.modules.kiosk.sections.G.questions.iecDisplay}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.g2 === option
                          ? form.g2 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="g2"
                            value={option}
                            checked={form.g2 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.g2 === option
                                ? form.g2 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.g2 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g2}
                </p>
              )}

              {form.g2 === t.global.yes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload photo of IEC display ({t.global.optionalNote?.split(":")[0]}):
                    <span className="text-gray-500 ml-1">
                      Recommended but not required
                    </span>
                  </label>

                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("g2Photo");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-500/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="g2Photo"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-500/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50 ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="g2Photo"
                    id="g2Photo"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.g2Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.g2Photo ? (
                      <div className="space-y-4">
                        {previewImages.g2Photo && (
                          <img
                            src={previewImages.g2Photo}
                            alt="IEC Preview"
                            className="mx-auto max-h-60 rounded-lg border shadow-sm"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <p className="text-gray-700 font-medium text-sm break-all">
                              {form.g2Photo.name}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Size: {(form.g2Photo.size / 1024).toFixed(1)} KB
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, g2Photo: null }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                g2Photo: null,
                              }));
                            }}
                            className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint} photo of IEC display
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Missing optional photo - Section may be marked as partial
                        </p>
                      </>
                    )}
                  </div>

                  {/* STATUS MESSAGE */}
                  {form.g2Photo && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-green-600 flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        Photo selected (will be fully completed)
                      </p>
                    </div>
                  )}

                  {errors.g2Photo && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.g2Photo}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* G3 - Waste Bin Cleaning */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Trash2 size={18} className="mr-2" />
                G3. {t.modules.kiosk.sections.G.questions.binCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.g3 === option
                          ? form.g3 === t.global.yes
                            ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                            : "bg-red-50 border-red-300 ring-2 ring-red-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="g3"
                            value={option}
                            checked={form.g3 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.g3 === option
                                ? form.g3 === t.global.yes
                                  ? "border-green-500 bg-green-500"
                                  : "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.g3 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g3}
                </p>
              )}

              {form.g3 === t.global.yes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload housekeeping checklist record ({t.global.optionalNote?.split(":")[0]})
                    <span className="text-gray-500 ml-1">
                      Recommended but not required
                    </span>
                  </label>

                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("g3Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-500/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="g3Document"
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-500/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="g3Document"
                    id="g3Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.g3Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.g3Document ? (
                      <div className="space-y-4">
                        {/* IMAGE PREVIEW */}
                        {previewImages.g3Document &&
                          form.g3Document.type.startsWith("image/") && (
                            <img
                              src={previewImages.g3Document}
                              alt="Checklist Preview"
                              className="mx-auto max-h-60 rounded-lg border shadow-sm"
                            />
                          )}

                        {/* PDF ICON */}
                        {form.g3Document.type === "application/pdf" && (
                          <div className="flex flex-col items-center text-red-600">
                            <FileText size={48} />
                            <p className="text-sm mt-2">
                              PDF Document Selected
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <p className="text-gray-700 font-medium text-sm break-all">
                              {form.g3Document.name}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Size: {(form.g3Document.size / 1024).toFixed(1)} KB
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                g3Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                g3Document: null,
                              }));
                            }}
                            className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint} housekeeping record
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Missing optional document - Section may be marked as partial
                        </p>
                      </>
                    )}
                  </div>

                  {errors.g3Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.g3Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* G4 - Single-Use Plastic */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2">
                G4. {t.modules.kiosk.sections.G.questions.singleUsePlastic}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.g4 === option
                          ? form.g4 === t.global.yes
                            ? "bg-red-50 border-red-300 ring-2 ring-red-100"
                            : "bg-green-50 border-green-300 ring-2 ring-green-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="g4"
                            value={option}
                            checked={form.g4 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.g4 === option
                                ? form.g4 === t.global.yes
                                  ? "border-red-500 bg-red-500"
                                  : "border-green-500 bg-green-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.g4 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g4}
                </p>
              )}

              {form.g4 === t.global.yes && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ Single-use plastic usage detected. Consider transitioning
                    to eco-friendly alternatives.
                  </p>
                </div>
              )}
            </div>

            {/* Completion Status - Only show when form is submittable */}
            {showCompletionStatus && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Info size={18} className="mr-2 text-blue-600" />
                    <span className="font-medium text-gray-700">
                      Submission Status:
                    </span>
                  </div>
                  <div>
                    {willBePartial ? (
                      <span className="text-amber-600 font-medium">
                        Will be Partial
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">
                        Will be Complete
                      </span>
                    )}
                  </div>
                </div>

                {willBePartial && (
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Missing optional information will mark this section as
                      partially completed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <div className="flex flex-col items-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                    isSubmitting || !isFormSubmittable
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : willBePartial
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t.global.submitting}
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t.global.submit_section_g}
                    </>
                  )}
                </button>

                {isFormSubmittable && willBePartial && (
                  <p className="text-xs text-amber-600 mt-1 text-right">
                    Will be saved as partially completed
                  </p>
                )}
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  * Required fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-amber-600 font-medium">
                  Optional fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-gray-600">
                  Missing optional fields = Partial completion
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CAMERA MODAL — ALWAYS AT END */}
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              const previewUrl = URL.createObjectURL(file);
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: previewUrl,
              }));
            }

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionGWasteManagement;